import { getLocaleString, getCountryCode } from 'utilities/locales';
import { getEnvironment } from 'utilities/environment';
import { VATStores } from 'constants/store_constants';
import Dinero from 'dinero.js';

// Taken from ISO 4217. https://www.currency-iso.org/dam/downloads/lists/list_one.xml
// Values represent number of decimal digits difference between major and minor units.
const CURRENCY_OPTIONS = {
  default: {
    precision: 2,
    format: '$0,0.00',
  },
  JPY: {
    precision: 0,
    format: '$0,0',
  },
};

const COUNTRY_CURRENCY = Object.freeze({
  US: 'USD',
  CA: 'CAD',
  AU: 'AUD',
  GB: 'GBP',
  JP: 'JPY',
  IE: 'EUR',
  FR: 'EUR',
  ES: 'EUR',
});

// The sq:environment meta tag can be absent on crawler-rendered pages
// (e.g. Google Storebot), which used to crash price components with
// "Cannot read properties of null (reading 'currency_code')". Fall back
// to the URL country's currency so the page still renders.
const getDefaultCurrencyCode = () =>
  getEnvironment()?.currency_code ||
  COUNTRY_CURRENCY[getCountryCode().toUpperCase()] ||
  'USD';

const getNumberFormat = ({ locale = getLocaleString(), currency }) => {
  const options = {
    style: 'currency',
    currency,
    ...(currency === 'CAD' && { currencyDisplay: 'narrowSymbol' }),
  };

  // Some older versions of Safari don't support currencyDisplay: narrowSymbol
  // so we need a fallback here
  try {
    return Intl.NumberFormat(locale, options);
  } catch {
    return Intl.NumberFormat(locale, { ...options, currencyDisplay: 'symbol' });
  }
};

const currencySymbol = (currency = getDefaultCurrencyCode()) => {
  const formatParts = getNumberFormat({ currency }).formatToParts();
  return formatParts.find((part) => part.type === 'currency').value;
};

const toCurrency = (number, currency = getDefaultCurrencyCode()) => {
  return getNumberFormat({ currency }).format(number);
};

const toMinorCurrencyUnit = (number, currency = getDefaultCurrencyCode()) => {
  const { precision } = CURRENCY_OPTIONS[currency] || CURRENCY_OPTIONS.default;
  const factor = Math.pow(10, precision);
  return Math.round(factor * number);
};

const toMinorCurrencyUnitWithSymbol = (
  number,
  currency = getDefaultCurrencyCode(),
) => {
  const { precision } = CURRENCY_OPTIONS[currency] || CURRENCY_OPTIONS.default;
  const factor = Math.pow(10, precision);

  return Dinero({
    amount: Math.round(factor * number),
    currency,
    precision,
  })
    .setLocale(getLocaleString())
    .toFormat('$0');
};

const toCurrencyWithoutSymbol = (
  number,
  locale = getLocaleString(),
  currency = getDefaultCurrencyCode(),
) => {
  const resolvedOptions = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).resolvedOptions();

  return number.toLocaleString(locale, {
    minimumFractionDigits: resolvedOptions.minimumFractionDigits,
    maximumFractionDigits: resolvedOptions.maximumFractionDigits,
  });
};

// Will create currency string using the minor currency unit
const toCurrencyWithMinorUnitAmount = (
  minorUnitAmount,
  currency = getDefaultCurrencyCode(),
) => {
  const { precision, format } =
    CURRENCY_OPTIONS[currency] || CURRENCY_OPTIONS.default;

  // Dinero throws "You must provide an integer." on fractional amounts
  // (root cause of the 2026-07-14 cart outage) — round like every other
  // helper in this file does.
  return Dinero({
    amount: Math.round(minorUnitAmount),
    currency,
    precision,
  })
    .setLocale(getLocaleString())
    .toFormat(format);
};

// Dinero may be overkill for simply converting from float to formatted currency,
// but it is handy for performing arithmetic operations on currency.
//
// [param] amount: may be a number or currency string
const toDinero = (amount, currency = getDefaultCurrencyCode()) => {
  const { precision } = CURRENCY_OPTIONS[currency] || CURRENCY_OPTIONS.default;

  return Dinero({
    amount: toMinorCurrencyUnit(amount),
    currency,
    precision,
  }).setLocale(getLocaleString());
};

const displayVAT = () => {
  return VATStores.includes(getCountryCode().toUpperCase());
};

export {
  getDefaultCurrencyCode,
  toMinorCurrencyUnit,
  currencySymbol,
  toCurrency,
  toCurrencyWithoutSymbol,
  toDinero,
  toCurrencyWithMinorUnitAmount,
  toMinorCurrencyUnitWithSymbol,
  displayVAT,
};
